<template>
	<div class="ai-points">
		<Card class="ai-card" title="筛选条件">
			<Form class="filter-form" :label-width="90">
				<div class="filter-row">
					<FormItem label="套餐名称">
						<Input v-model.trim="search.name" placeholder="请输入套餐名称" clearable />
					</FormItem>
					<FormItem label="上架状态">
						<Select v-model="search.status" placeholder="请选择上架状态" clearable>
							<Option :value="-1">全部</Option>
							<Option :value="1">已上架</Option>
							<Option :value="0">已下架</Option>
						</Select>
					</FormItem>
					<FormItem label="购买限制">
						<Select v-model="search.purchase_limit" placeholder="请选择购买限制" clearable>
							<Option :value="-1">全部</Option>
							<Option v-for="item in purchaseLimitOptions" :key="item.value" :value="item.value">
								{{ item.label }}
							</Option>
						</Select>
					</FormItem>
					<div class="btn-group">
						<Button type="primary" icon="ios-search" @click="handleSearch">搜索</Button>
						<Button icon="ios-refresh" @click="resetSearch">重置</Button>
						<Button type="success" icon="md-add" @click="openModal()">添加套餐</Button>
					</div>
				</div>
			</Form>
		</Card>

		<Card class="ai-card" title="套餐列表">
			<Table
				:data="tableData"
				:columns="columns"
				:loading="loading"
				border
				stripe
				:no-data-text="'暂无套餐数据'"
			>
				<template slot="points" slot-scope="{ row }">
					<div class="points-line">
						<span>基础：{{ row.base_points }}</span>
						<span>赠送：{{ row.bonus_points }}</span>
						<span>合计：{{ totalPoints(row) }}</span>
					</div>
				</template>
				<template slot="status" slot-scope="{ row }">
					<Tag :color="row.status === 1 ? 'success' : 'default'">
						{{ row.status === 1 ? '已上架' : '已下架' }}
					</Tag>
				</template>
				<template slot="purchase_limit" slot-scope="{ row }">
					{{ getPurchaseLimitText(row.purchase_limit) }}
				</template>
				<template slot="operate" slot-scope="{ row }">
					<Button type="primary" size="small" @click="openModal(row)">编辑</Button>
					<Button
							:type="row.status === 1 ? 'warning' : 'success'"
							size="small"
							style="margin:0 8px;"
							@click="toggleStatus(row)"
					>
						{{ row.status === 1 ? '下架' : '上架' }}
					</Button>
					<Button type="error" size="small" @click="deletePackage(row)">删除</Button>
				</template>
			</Table>
			<div class="table-footer">
				<Page
					:total="search.total"
					:current="search.page"
					:page-size="search.pageSize"
					:page-size-opts="[15, 30, 50]"
					show-total
					show-sizer
					@on-change="pageChange"
					@on-page-size-change="pageSizeChange"
				/>
			</div>
		</Card>

		<Modal
			:title="modal.title"
			v-model="modal.show"
			:mask-closable="false"
			width="520"
			@on-cancel="closeModal"
		>
			<Form
				ref="packageForm"
				:model="modal.data"
				:rules="formRules"
				:label-width="110"
				label-colon
			>
				<FormItem label="套餐编码" v-if="modal.data.package_code">
					<Input :value="modal.data.package_code" disabled />
				</FormItem>
				<FormItem label="套餐名称" prop="name">
					<Input v-model.trim="modal.data.name" placeholder="请输入套餐名称" maxlength="50" clearable />
				</FormItem>
				<FormItem label="套餐简介">
					<Input
						type="textarea"
						v-model="modal.data.description"
						placeholder="请输入套餐简介"
						:autosize="{ minRows: 2, maxRows: 4 }"
						maxlength="200"
						show-word-limit
					/>
				</FormItem>
				<FormItem label="套餐价格" prop="price">
					<InputNumber
						v-model="modal.data.price"
						:min="0"
						:precision="2"
						placeholder="请输入套餐价格"
						style="width:100%;"
					/>
				</FormItem>
				<FormItem label="基础资源点" prop="base_points">
					<InputNumber
						v-model="modal.data.base_points"
						:min="0"
						:step="1"
						placeholder="请输入基础资源点"
						style="width:100%;"
					/>
				</FormItem>
				<FormItem label="赠送资源点">
					<InputNumber
						v-model="modal.data.bonus_points"
						:min="0"
						:step="1"
						placeholder="请输入赠送资源点"
						style="width:100%;"
					/>
				</FormItem>
				<FormItem label="有效期(天)" prop="valid_days">
					<InputNumber
						v-model="modal.data.valid_days"
						:min="1"
						:step="1"
						placeholder="请输入有效期天数"
						style="width:100%;"
					/>
				</FormItem>
				<FormItem label="购买限制">
					<Select v-model="modal.data.purchase_limit" placeholder="请选择购买限制">
						<Option v-for="item in purchaseLimitOptions" :key="item.value" :value="item.value">
							{{ item.label }}
						</Option>
					</Select>
				</FormItem>
				<FormItem label="排序值">
					<InputNumber
						v-model="modal.data.sort"
						:min="0"
						:step="1"
						placeholder="请输入排序值"
						style="width:100%;"
					/>
				</FormItem>
				<FormItem label="备注">
					<Input
						type="textarea"
						v-model="modal.data.remark"
						placeholder="请输入备注信息"
						:autosize="{ minRows: 2, maxRows: 4 }"
						maxlength="200"
						show-word-limit
					/>
				</FormItem>
			</Form>
			<template #footer>
				<Button @click="closeModal">取消</Button>
				<Button type="primary" :loading="modal.loading" @click="savePackage">确认</Button>
			</template>
		</Modal>
	</div>
</template>

<script>
const defaultModalData = () => ({
	id: null,
	package_code: '',
	name: '',
	description: '',
	price: 0,
	base_points: 0,
	bonus_points: 0,
	valid_days: 365,
	purchase_limit: 0,
	sort: 99,
	status: 1,
	remark: ''
})

const defaultSearch = () => ({
	total: 0,
	pageSize: 15,
	page: 1,
	name: '',
	status: -1,
	purchase_limit: -1
})

export default {
	data() {
		return {
			search: defaultSearch(),
			loading: false,
			tableData: [],
			purchaseLimitOptions: [
				{ value: 0, label: '不限' },
				{ value: 1, label: '仅能购买一次' },
				{ value: 2, label: '周期限制' }
			],
			modal: {
				show: false,
				title: '添加套餐',
				loading: false,
				data: defaultModalData()
			},
			formRules: {
				name: [{ required: true, message: '请输入套餐名称', trigger: 'blur' }],
				price: [{ required: true, type: 'number', message: '请输入套餐价格', trigger: 'change' }],
				base_points: [{ required: true, type: 'number', message: '请输入基础资源点', trigger: 'change' }],
				valid_days: [{ required: true, type: 'number', message: '请输入有效期天数', trigger: 'change' }]
			}
		}
	},
	computed: {
		columns() {
			return [
				{ title: '套餐名称', key: 'name', minWidth: 160, ellipsis: true, tooltip: true },
				{
					title: '售价(元)',
					key: 'price',
					minWidth: 110,
					align: 'right',
					render: (h, { row }) => h('span', this.formatPrice(row.price))
				},
				{ title: '资源点', slot: 'points', minWidth: 200 },
				{ title: '有效期(天)', key: 'valid_days', minWidth: 110, align: 'center' },
				{ title: '购买限制', slot: 'purchase_limit', minWidth: 120 },
				{ title: '状态', slot: 'status', minWidth: 100, align: 'center' },
				{ title: '排序', key: 'sort', minWidth: 90, align: 'center' },
				{ title: '创建时间', key: 'created_at', minWidth: 170 },
				{ title: '操作', slot: 'operate', minWidth: 220, align: 'center' }
			]
		}
	},
	created() {
		this.init()
	},
	methods: {
		handleSearch() {
			this.search.page = 1
			this.init()
		},
		resetSearch() {
			const reset = defaultSearch()
			reset.pageSize = this.search.pageSize
			Object.assign(this.search, reset)
			this.init()
		},
		formatPrice(value) {
			const num = Number(value || 0)
			return num.toFixed(2)
		},
		totalPoints(row) {
			return Number(row.base_points || 0) + Number(row.bonus_points || 0)
		},
		getPurchaseLimitText(value) {
			const match = this.purchaseLimitOptions.find(item => item.value === value)
			return match ? match.label : '-'
		},
		init() {
			this.loading = true
			const params = Object.assign({}, this.search)
			this.requestApi('/adm/get_ai_points_package_list', { search: params })
				.then(res => {
					if (res.status === 1) {
						this.tableData = res.data.data || []
						this.search.total = res.data.total || 0
					} else {
						this.alertErr(res.msg || '获取数据失败')
					}
				})
				.finally(() => {
					this.loading = false
				})
		},
		pageChange(page) {
			this.search.page = page
			this.init()
		},
		pageSizeChange(size) {
			this.search.pageSize = size
			this.search.page = 1
			this.init()
		},
		openModal(row) {
			this.modal.show = true
			this.modal.loading = false
			const data = defaultModalData()
			if (row) {
				this.modal.title = '编辑套餐'
				// 完整复制所有字段，确保数据回显完整
				Object.assign(data, {
					id: row.id || null,
					package_code: row.package_code || '',
					name: row.name || '',
					description: row.description || '',
					price: Number(row.price || 0),
					base_points: Number(row.base_points || 0),
					bonus_points: Number(row.bonus_points || 0),
					valid_days: Number(row.valid_days || 0),
					purchase_limit: row.purchase_limit !== undefined ? row.purchase_limit : 0,
					sort: Number(row.sort || 0),
					status: row.status !== undefined ? row.status : 1,
					remark: row.remark || ''
				})
			} else {
				this.modal.title = '添加套餐'
			}
			this.modal.data = data
			this.$nextTick(() => {
				let formRef = this.$refs.packageForm
				if (Array.isArray(formRef)) formRef = formRef[0]
				if (formRef) {
					// 只清除验证状态，不重置字段值
					if (typeof formRef.clearValidate === 'function') {
						formRef.clearValidate()
					}
				}
			})
		},
		closeModal() {
			this.modal.show = false
			this.modal.loading = false
			this.$nextTick(() => {
				let formRef = this.$refs.packageForm
				if (Array.isArray(formRef)) formRef = formRef[0]
				if (formRef && typeof formRef.resetFields === 'function') {
					formRef.resetFields()
				}
			})
			this.modal.data = defaultModalData()
		},
		savePackage() {
			this.$refs.packageForm.validate(valid => {
				if (!valid) return
				this.modal.loading = true
				const payload = Object.assign({}, this.modal.data)
				payload.price = Number(payload.price || 0)
				payload.base_points = Number(payload.base_points || 0)
				payload.bonus_points = Number(payload.bonus_points || 0)
				payload.valid_days = Number(payload.valid_days || 0)
				payload.sort = Number(payload.sort || 0)
				this.requestApi('/adm/save_ai_points_package', { data: payload })
					.then(res => {
						if (res.status === 1) {
							this.alertSucc('保存成功')
							this.closeModal()
							this.init()
						} else {
							this.alertErr(res.msg || '保存失败')
						}
					})
					.finally(() => {
						this.modal.loading = false
					})
			})
		},
		toggleStatus(row) {
			const nextText = row.status === 1 ? '确认下架该套餐？' : '确认上架该套餐？'
			this.$Modal.confirm({
				title: '提示',
				content: nextText,
				onOk: () => {
					this.requestApi('/adm/change_status_ai_points_package', {
						id: row.id,
						status: row.status
					}).then(res => {
						if (res.status === 1) {
							this.alertSucc(row.status === 1 ? '下架成功' : '上架成功')
							this.init()
						} else {
							this.alertErr(res.msg || '操作失败')
						}
					})
				}
			})
		},
		deletePackage(row) {
			this.$Modal.confirm({
				title: '删除确认',
				content: '删除后不可恢复，是否继续？',
				onOk: () => {
					this.requestApi('/adm/delete_ai_points_package', { id: row.id }).then(res => {
						if (res.status === 1) {
							this.alertSucc('删除成功')
							this.init()
						} else {
							this.alertErr(res.msg || '删除失败')
						}
					})
				}
			})
		}
	}
}
</script>

<style scoped>
.ai-points {
	padding-bottom: 16px;
}

.ai-card {
	margin-bottom: 16px;
}

.filter-form {
	padding: 0 8px;
}

.filter-row {
	display: flex;
	flex-wrap: wrap;
	gap: 12px 16px;
	align-items: flex-end;
}

.filter-row .ivu-form-item {
	flex: 1 1 240px;
	margin-bottom: 12px;
}

.btn-group {
	display: flex;
	gap: 8px;
	margin-bottom: 12px;
}

.points-line {
	display: flex;
	gap: 12px;
	font-size: 12px;
	color: #606266;
}

.table-footer {
	margin-top: 16px;
	text-align: right;
}
</style>
